/**
 * Feed-related types
 */

export interface RelatedArticle {
  item_id: string
  title: string
  title_he?: string
  source_id: string
  source_name?: string
  published_date: string
}

export interface NewsItem {
  id: string // Backend uses 'id' as alias for item_id
  item_id?: string // Keep for backward compatibility
  title: string
  title_he?: string // Hebrew title (when translated)
  description: string
  content: string | null
  content_translated?: boolean // true when content is translated plain text
  translation_status?: string | null // 'partial' | 'chunked_pending' | null (complete)
  source_url: string
  source_id: string
  source_name?: string // Optional, not returned by backend
  published_date: string
  created_at: string
  labels?: string[] // Backend returns labels, not categories
  source_tags?: string[] // AWS service/product tags
  share_count?: number // Placeholder for MVP
  related_articles?: RelatedArticle[] // Related articles (populated by FAISS computation)
}

export type DraftFilter = 'all' | 'drafted' | 'new'

export interface FeedFilters {
  topics: string[] // Maps to labels in backend
  tags: string[] // AWS service/product tags (source_tags)
  dateRange: {
    start: Date | null
    end: Date | null
  }
  sources: string[]
  searchQuery: string
  sortBy: 'date' | 'relevance' | 'popularity'
  draftFilter: DraftFilter  // client-side only — uses draftedItemIds
}

export interface FeedState {
  items: NewsItem[]
  filters: FeedFilters
  isLoading: boolean
  error: string | null
  hasMore: boolean
  page: number
}

export interface FeedContextType extends FeedState {
  setFilters: (filters: Partial<FeedFilters> | ((prev: FeedFilters) => FeedFilters)) => void
  clearFilters: () => void
  loadMore: () => void
  refresh: () => void

  // Language preference
  lang: string
  setLang: (lang: string) => void

  // Save functionality
  savedItemIds: Set<string>
  saveItem: (itemId: string) => Promise<void>
  unsaveItem: (itemId: string) => Promise<void>

  // Sources map: source_id → human-readable name
  sourcesMap: Record<string, string>
}

export const DEFAULT_FILTERS: FeedFilters = {
  topics: [],
  tags: [],
  dateRange: { start: null, end: null },
  sources: [],
  searchQuery: '',
  sortBy: 'date',
  draftFilter: 'all',
}
